// utils
import { List, Map } from 'immutable';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {
  ITEM_STATUS_GROUP_ORDER,
  ITEM_STATUS_GROUPS,
} from '../../containers/Inventory/itemStatusGroups';
import { isUnprocessedRequest } from '../../containers/Storage/components/ServiceRequests/requestHelper';
// requests
import api from '../../utils/api';
import { toastResponseError } from '../../utils/responseMessageHelper';
import {
  addParcels,
  addParcelsError,
  addParcelsSuccess,
} from '../reducers/cart';
import { openRightPanel } from '../reducers/rightPanel';
// redux
import {
  ADD_PARCELS_TO_CART,
  catchStorageError,
  defaultParams,
  defaultParamsExpected,
  fetchStorage,
  finaliseParcelsToCart,
  GET_EXPECTED_ITEMS,
  GET_INVENTORY_ITEMS,
  GET_INVENTORY_ITEMS_PAGE,
  GET_STORAGE,
  getShipment,
  normalizeItems,
  SELECT_ALL,
  selectParcel,
  setItemsConsolidation,
  updateExpectedItemsSearch,
  updateInventoryItemsGroup,
  updateStorage,
  updateTransferringItems,
} from '../reducers/storage';
import { cartSelectForOutgoing } from '../selectors/cartSelectors';
import {
  selectStorage,
  storageSelectActiveStorageBranch,
} from '../selectors/storageSelectors';

/* worker-saga */
function* getStorageTransferringItems() {
  try {
    const activeStorageBranchId = yield select(
      storageSelectActiveStorageBranch,
    );

    return yield call(api.shipments.getTransferring, {
      filter: {
        'q[branch_id_eq]': activeStorageBranchId,
      },
    });
  } catch (response) {
    return new Map({
      data: new List(),
    });
  }
}

function* onGetStorage({ payload }) {
  try {
    const activeStorageBranchId = yield select(
      storageSelectActiveStorageBranch,
    );

    const params = payload.size ? payload : defaultParams;

    yield put(fetchStorage(params));

    const [transferringItemsResponse, response] = yield all([
      call(getStorageTransferringItems),
      call(
        api.shipments.getStorage,
        payload
          .setIn(['filter', 'q[branch_id_eq]'], activeStorageBranchId)
          .toJS(),
      ),
    ]);

    yield put(updateTransferringItems(transferringItemsResponse));
    yield put(updateStorage(response.set('params', params)));
  } catch (response) {
    toastResponseError(response);
    catchStorageError(response);
  }
}

/* worker-saga */
// One request per category, scoped to that category's statuses. The response is
// tagged so the reducer knows which category and page it belongs to.
function* getInventoryItemsGroup({ group, page, filter, branchId }) {
  const response = yield call(api.items.getInventoryItems, {
    page,
    filter: {
      ...filter,
      'q[status_in]': ITEM_STATUS_GROUPS[group],
      'q[branch_id_eq]': branchId,
    },
  });

  return response.set('group', group).set('page', page);
}

function* onGetInventoryItems({ payload }) {
  try {
    const branchId = yield select(storageSelectActiveStorageBranch);
    const filter = payload.get('filter') || {};
    const page = payload.get('page', 1);
    // Spread — `yield all(<ImmutableList>)` yields nothing (see kbTreeLoader).
    const groups = [...(payload.get('groups') || ITEM_STATUS_GROUP_ORDER)];

    const responses = yield all(
      groups.map((group) =>
        call(getInventoryItemsGroup, { group, page, filter, branchId }),
      ),
    );

    yield all(
      responses.map((response) => put(updateInventoryItemsGroup(response))),
    );
  } catch (response) {
    toastResponseError(response);
    yield put(catchStorageError(response));
  }
}

function* onGetInventoryItemsPage({ payload: group }) {
  try {
    const branchId = yield select(storageSelectActiveStorageBranch);
    const storage = yield select(selectStorage);
    const filter = storage.get('inventoryItemsFilter', Map()).toJS();
    const page = storage.getIn(['inventoryItemsGroups', group, 'page'], 1) + 1;

    const response = yield call(getInventoryItemsGroup, {
      group,
      page,
      filter,
      branchId,
    });
    yield put(updateInventoryItemsGroup(response));
  } catch (response) {
    toastResponseError(response);
    yield put(catchStorageError(response));
  }
}

function* onGetExpectedItems({ payload }) {
  try {
    const params = payload.size ? payload : defaultParamsExpected;
    const response = yield call(api.expected.getExpectedItems, params.toJS());
    yield put(updateExpectedItemsSearch(response.set('params', params)));
  } catch (response) {
    toastResponseError(response);
    catchStorageError(response);
  }
}

function* onSelectAll() {
  const storage = yield select(selectStorage);

  const items = storage.get('items');
  const totalAmount = storage.get('totalAmount');
  const params = storage.get('params');
  const getSelectedItems = (items) =>
    items.reduce(
      (selectedItems, item) =>
        !isUnprocessedRequest(item.get('serviceRequestCount')) &&
        item.get('prohibited') !== true
          ? selectedItems.push(item.get('id'))
          : selectedItems,
      List(),
    );

  if (totalAmount === items.size) {
    yield put(selectParcel(getSelectedItems(items)));
    return null;
  }

  try {
    const fetchParams = params
      .set('page', 1)
      .setIn(['filter', 'per_page'], totalAmount);

    const activeStorageBranchId = yield select(
      storageSelectActiveStorageBranch,
    );

    yield put(fetchStorage(fetchParams));
    const response = yield call(
      api.shipments.getStorage,
      fetchParams
        .setIn(['filter', 'q[branch_id_eq]'], activeStorageBranchId)
        .toJS(),
    );

    yield put(updateStorage(response.set('params', fetchParams)));
    const selectedIds = getSelectedItems(normalizeItems(response.get('data')));

    yield put(selectParcel(selectedIds));
  } catch (response) {
    toastResponseError(response);
    catchStorageError(response);
  }
}

function* onAddParcelsToCart({
  payload: { id, useCartItemsApi, decant_ids, shipmentId, openedFromStorage },
}) {
  try {
    yield put(addParcels());
    const response = useCartItemsApi
      ? yield call(api.cart.addInventoryParcelItems, {
          id,
          decant_ids,
        })
      : yield call(api.cart.addParcels, { id: id });

    if (useCartItemsApi) {
      const cartSku = response.getIn(['data', 'sku']);
      const cartId = response.getIn(['data', 'id']);
      if (cartSku && cartId) {
        yield put(
          setItemsConsolidation({
            shipmentId,
            itemIds: id,
            consolidation: { id: cartId, sku: cartSku },
          }),
        );
      }
      if (shipmentId) {
        yield put(getShipment(shipmentId));
      }
    }
    if (!useCartItemsApi) {
      try {
        const activeStorageBranchId = yield select(
          storageSelectActiveStorageBranch,
        );

        const params = defaultParams;
        yield put(fetchStorage(params.set('isAddParcelsToCart', true)));
        const responseStorage = yield call(
          api.shipments.getStorage,
          params
            .setIn(['filter', 'q[branch_id_eq]'], activeStorageBranchId)
            .toJS(),
        );
        yield put(updateStorage(responseStorage.set('params', params)));
      } catch (responseStorage) {
        toastResponseError(responseStorage);
        catchStorageError(responseStorage);
      }
    }

    const cart = yield select(cartSelectForOutgoing);
    yield put(
      openRightPanel(
        'NOT_FINISHED',
        Map({
          detailedItemID: cart.get('id'),
          parcel: cart,
          openedFromStorage,
        }),
      ),
    );

    yield put(addParcelsSuccess(response));
    yield put(finaliseParcelsToCart());
  } catch (response) {
    yield put(addParcelsError());
    toastResponseError(response);
  }
}

/* watcher-saga */
function* watchStorageActions() {
  yield takeLatest(GET_STORAGE, onGetStorage);
  yield takeLatest(GET_INVENTORY_ITEMS, onGetInventoryItems);
  // takeEvery: categories paginate independently, so one category's fetch must
  // not cancel another's.
  yield takeEvery(GET_INVENTORY_ITEMS_PAGE, onGetInventoryItemsPage);
  yield takeLatest(GET_EXPECTED_ITEMS, onGetExpectedItems);
  yield takeLatest(SELECT_ALL, onSelectAll);
  yield takeLatest(ADD_PARCELS_TO_CART, onAddParcelsToCart);
}

export default watchStorageActions;
